/**
 * Se l'app gira come applicazione installata, e su che tipo di dispositivo.
 *
 * ── Perché conta ──────────────────────────────────────────────────────────
 *
 * Perché su iPhone **le notifiche push esistono solo per le PWA aggiunte alla
 * schermata Home**. In una scheda di Safari non esiste nemmeno l'oggetto
 * `PushManager`: metà dell'app promette cose che non può mantenere.
 */

/** `true` se la finestra è quella di un'app installata, non una scheda. */
export function isInstalled(): boolean {
  return (
    window.matchMedia("(display-mode: standalone)").matches ||
    window.matchMedia("(display-mode: fullscreen)").matches ||
    window.matchMedia("(display-mode: minimal-ui)").matches ||
    // Il modo di Safari di dire la stessa cosa, da prima dello standard.
    (window.navigator as { standalone?: boolean }).standalone === true
  );
}

export type Platform = "ios" | "android" | "desktop";

export function platform(): Platform {
  const agent = window.navigator.userAgent;

  // `maxTouchPoints` perché dall'iPadOS 13 Safari si dichiara «Macintosh»: il
  // solo user agent classificherebbe ogni iPad come scrivania.
  if (/iPhone|iPod/.test(agent)) return "ios";
  if (/iPad/.test(agent)) return "ios";
  if (/Macintosh/.test(agent) && window.navigator.maxTouchPoints > 1) return "ios";
  if (/Android/.test(agent)) return "android";

  return "desktop";
}

/**
 * Se questa apertura va fermata e indirizzata all'installazione.
 *
 * ── Solo il telefono ──────────────────────────────────────────────────────
 *
 * Sulla scrivania **no**, e non è un'esitazione: Safari per Mac non installa le
 * PWA nel modo che ci serve, e bloccare lì significherebbe rendere l'app
 * inutilizzabile da computer per ottenere notifiche che da computer nessuno
 * aspetta. Il vincolo di iOS è di iOS: la cura va dove sta il problema.
 *
 * ── Le pagine esenti ──────────────────────────────────────────────────────
 *
 * Il **link di invito** è il punto d'ingresso di chi non ha ancora l'app —
 * bloccarlo vorrebbe dire che nessuno può più entrare in un gruppo. E la
 * diagnostica resta raggiungibile per la stessa ragione per cui esiste: serve
 * quando qualcosa non va, cioè proprio quando l'app potrebbe non partire.
 */
const EXEMPT = [/^\/i\//, /^\/invito/, /^\/debug/];

export function shouldRequireInstall(pathname: string): boolean {
  if (isInstalled()) return false;
  if (platform() === "desktop") return false;

  return !EXEMPT.some((pattern) => pattern.test(pathname));
}
